import React, { useState, useMemo } from 'react';
import { Table, Button, Badge, Modal, Alert, Form, Row, Col, InputGroup } from 'react-bootstrap';

const BASList = ({ forms, onEdit, onDelete, onATOSubmission }) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formToDelete, setFormToDelete] = useState(null);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterText, setFilterText] = useState('');
  const [validationResult, setValidationResult] = useState(null);
  const [validatingForm, setValidatingForm] = useState(null);
  const [formValidationStatus, setFormValidationStatus] = useState({});
  const [isValidatingAll, setIsValidatingAll] = useState(false);
  const [showValidationDetails, setShowValidationDetails] = useState(false);
  const [selectedFormValidation, setSelectedFormValidation] = useState(null);

  const handleDelete = (form) => {
    setFormToDelete(form);
    setShowDeleteModal(true);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handleValidateATO = async (form) => {
    setValidatingForm(form.id);
    setValidationResult(null);

    try {
      const response = await fetch('/api/validate-ato', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          abn: form.abn,
          gst_sales: form.gst_sales,
          gst_purchases: form.gst_purchases,
          gst_payable: form.gst_payable,
          period_start: form.period_start,
          period_end: form.period_end
        }),
      });

      const result = await response.json();
      setValidationResult({ form, result });
    } catch (error) {
      setValidationResult({
        form,
        result: {
          valid: false,
          errors: ['Validation failed: ' + error.message],
          warnings: []
        }
      });
    } finally {
      setValidatingForm(null);
    }
  };

  const clearValidation = () => {
    setValidationResult(null);
  };

  const validateAllForms = async () => {
    setIsValidatingAll(true);
    const validationStatus = {};

    try {
      // Validate each form individually
      for (const form of forms) {
        if (form.abn) {
          try {
            const response = await fetch('/api/validate-abn', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ abn: form.abn }),
            });

            const result = await response.json();
            validationStatus[form.id] = {
              valid: result.valid,
              abrData: result.abrLookup,
              error: result.error,
              lastValidated: new Date().toISOString()
            };
          } catch (error) {
            validationStatus[form.id] = {
              valid: false,
              error: 'Validation failed: ' + error.message,
              lastValidated: new Date().toISOString()
            };
          }
        } else {
          validationStatus[form.id] = {
            valid: false,
            error: 'No ABN provided',
            lastValidated: new Date().toISOString()
          };
        }
      }

      setFormValidationStatus(validationStatus);
    } catch (error) {
      console.error('Error validating forms:', error);
    } finally {
      setIsValidatingAll(false);
    }
  };

  const getValidationBadge = (formId) => {
    const status = formValidationStatus[formId];
    if (!status) {
      return <Badge bg="secondary">Not Validated</Badge>;
    }

    if (status.valid) {
      return (
        <Badge 
          bg="success" 
          style={{ cursor: 'pointer' }}
          onClick={() => showValidationDetailsModal(formId)}
        >
          Valid
        </Badge>
      );
    } else {
      return (
        <Badge 
          bg="danger" 
          style={{ cursor: 'pointer' }}
          onClick={() => showValidationDetailsModal(formId)}
        >
          Invalid
        </Badge>
      );
    }
  };

  const showValidationDetailsModal = (formId) => {
    const status = formValidationStatus[formId];
    if (status) {
      setSelectedFormValidation({ formId, status });
      setShowValidationDetails(true);
    }
  };

  // Filter and sort forms
  const filteredAndSortedForms = useMemo(() => {
    let filtered = forms.filter(form => {
      if (!filterText) return true;
      const searchText = filterText.toLowerCase();
      return (
        form.business_name?.toLowerCase().includes(searchText) ||
        form.abn?.toLowerCase().includes(searchText) ||
        form.period_start?.toLowerCase().includes(searchText) ||
        form.period_end?.toLowerCase().includes(searchText) ||
        form.status?.toLowerCase().includes(searchText)
      );
    });

    return filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'business_name':
          aValue = a.business_name || '';
          bValue = b.business_name || '';
          break;
        case 'abn':
          aValue = a.abn || '';
          bValue = b.abn || '';
          break;
        case 'period_start':
          aValue = new Date(a.period_start);
          bValue = new Date(b.period_start);
          break;
        case 'total_amount':
          aValue = parseFloat(a.total_amount) || 0;
          bValue = parseFloat(b.total_amount) || 0;
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        default:
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [forms, filterText, sortBy, sortOrder]);

  const confirmDelete = async () => {
    if (!formToDelete) return;

    try {
      const response = await fetch(`/api/bas-forms/${formToDelete.id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        alert('Form deleted successfully!');
        onDelete && onDelete();
      } else {
        const error = await response.json();
        alert('Error: ' + error.error);
      }
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setShowDeleteModal(false);
      setFormToDelete(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-AU');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { variant: 'secondary', text: 'Draft' },
      ready: { variant: 'warning', text: 'Ready' },
      submitted: { variant: 'success', text: 'Submitted' },
      error: { variant: 'danger', text: 'Error' }
    };

    const config = statusConfig[status] || statusConfig.draft;
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  if (forms.length === 0) {
    return (
      <div className="form-section">
        <Alert variant="info">
          <i className="fas fa-info-circle me-2"></i>
          No BAS forms found. Create your first form using the "New Form" tab.
        </Alert>
      </div>
    );
  }

  return (
    <div className="form-section">
      <h3>
        <i className="fas fa-list me-2"></i>
        BAS Forms List
      </h3>
      
      {/* Filter and Sort Controls */}
      <Row className="mb-3">
        <Col md={4}>
          <InputGroup>
            <InputGroup.Text>
              <i className="fas fa-search"></i>
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Filter by business name, ABN, period, or status..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col md={3}>
          <Form.Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="created_at">Sort by Created Date</option>
            <option value="business_name">Sort by Business Name</option>
            <option value="abn">Sort by ABN</option>
            <option value="period_start">Sort by Period</option>
            <option value="total_amount">Sort by Amount</option>
            <option value="status">Sort by Status</option>
          </Form.Select>
        </Col>
        <Col md={2}>
          <Button
            variant="outline-secondary"
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="w-100"
          >
            <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
            {sortOrder === 'asc' ? 'Asc' : 'Desc'}
          </Button>
        </Col>
        <Col md={3}>
          <Button
            variant="outline-info"
            onClick={validateAllForms}
            disabled={isValidatingAll}
            className="w-100"
          >
            {isValidatingAll ? (
              <>
                <i className="fas fa-spinner fa-spin me-1"></i>
                Validating...
              </>
            ) : (
              <>
                <i className="fas fa-check-double me-1"></i>
                Validate All ABNs
              </>
            )}
          </Button>
        </Col>
      </Row>
      
      <div className="table-responsive">
        <Table striped hover>
          <thead>
            <tr>
              <th>ID</th>
              <th 
                className="sortable" 
                onClick={() => handleSort('business_name')}
                style={{ cursor: 'pointer' }}
              >
                Business Name
                {sortBy === 'business_name' && (
                  <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'} ms-1`}></i>
                )}
              </th>
              <th 
                className="sortable" 
                onClick={() => handleSort('abn')}
                style={{ cursor: 'pointer' }}
              >
                ABN
                {sortBy === 'abn' && (
                  <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'} ms-1`}></i>
                )}
              </th>
              <th 
                className="sortable" 
                onClick={() => handleSort('period_start')}
                style={{ cursor: 'pointer' }}
              >
                Period
                {sortBy === 'period_start' && (
                  <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'} ms-1`}></i>
                )}
              </th>
              <th 
                className="sortable" 
                onClick={() => handleSort('total_amount')}
                style={{ cursor: 'pointer' }}
              >
                Total Amount
                {sortBy === 'total_amount' && (
                  <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'} ms-1`}></i>
                )}
              </th>
              <th 
                className="sortable" 
                onClick={() => handleSort('status')}
                style={{ cursor: 'pointer' }}
              >
                Status
                {sortBy === 'status' && (
                  <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'} ms-1`}></i>
                )}
              </th>
              <th>ABN Validation</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedForms.map((form) => (
              <tr key={form.id}>
                <td>{form.id}</td>
                <td>{form.business_name}</td>
                <td>{form.abn}</td>
                <td>
                  {formatDate(form.period_start)} - {formatDate(form.period_end)}
                </td>
                <td className="text-end">
                  <strong>{formatCurrency(form.total_amount)}</strong>
                </td>
                <td>{getStatusBadge(form.status)}</td>
                <td>{getValidationBadge(form.id)}</td>
                <td>{formatDate(form.created_at)}</td>
                <td>
                  <div className="btn-group" role="group">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => onEdit && onEdit(form)}
                      title="Edit Form"
                    >
                      <i className="fas fa-edit"></i>
                    </Button>
                    <Button
                      variant="outline-success"
                      size="sm"
                      onClick={() => handleValidateATO(form)}
                      disabled={validatingForm === form.id}
                      title="Validate ATO Compliance"
                    >
                      {validatingForm === form.id ? (
                        <i className="fas fa-spinner fa-spin"></i>
                      ) : (
                        <i className="fas fa-check-circle"></i>
                      )}
                    </Button>
                    <Button
                      variant="outline-warning"
                      size="sm"
                      onClick={() => onATOSubmission && onATOSubmission(form)}
                      title="Submit to ATO"
                    >
                      <i className="fas fa-paper-plane"></i>
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDelete(form)}
                      title="Delete Form"
                    >
                      <i className="fas fa-trash"></i>
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the BAS form for{' '}
          <strong>{formToDelete?.business_name}</strong> (Period: {formToDelete ? formatDate(formToDelete.period_start) : ''} - {formToDelete ? formatDate(formToDelete.period_end) : ''})?
          <br />
          <br />
          <Alert variant="warning">
            <i className="fas fa-exclamation-triangle me-2"></i>
            This action cannot be undone.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            <i className="fas fa-trash me-1"></i>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ATO Validation Results Modal */}
      <Modal show={!!validationResult} onHide={clearValidation} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-check-circle me-2"></i>
            ATO Validation Results
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {validationResult && (
            <>
              <h6>Form: {validationResult.form.business_name}</h6>
              <p className="text-muted">
                Period: {formatDate(validationResult.form.period_start)} - {formatDate(validationResult.form.period_end)}
              </p>
              
              {validationResult.result.valid ? (
                <Alert variant="success">
                  <i className="fas fa-check-circle me-2"></i>
                  <strong>Validation Passed!</strong>
                  <br />
                  This form appears to be compliant with ATO requirements.
                </Alert>
              ) : (
                <Alert variant="danger">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  <strong>Validation Failed!</strong>
                  <br />
                  Please review and fix the following issues:
                </Alert>
              )}

              {validationResult.result.errors && validationResult.result.errors.length > 0 && (
                <div className="mb-3">
                  <h6 className="validation-error">
                    <i className="fas fa-times-circle me-1"></i>
                    Errors:
                  </h6>
                  <ul className="validation-error">
                    {validationResult.result.errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}

              {validationResult.result.warnings && validationResult.result.warnings.length > 0 && (
                <div className="mb-3">
                  <h6 className="validation-warning">
                    <i className="fas fa-exclamation-triangle me-1"></i>
                    Warnings:
                  </h6>
                  <ul className="validation-warning">
                    {validationResult.result.warnings.map((warning, index) => (
                      <li key={index}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}

              {validationResult.result.valid && validationResult.result.warnings && validationResult.result.warnings.length === 0 && (
                <div className="validation-result">
                  <Alert variant="success">
                    <i className="fas fa-thumbs-up me-2"></i>
                    No issues found. This form is ready for ATO submission.
                  </Alert>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={clearValidation}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ABN Validation Details Modal */}
      <Modal show={showValidationDetails} onHide={() => setShowValidationDetails(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-search me-2"></i>
            ABN Validation Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedFormValidation && (
            <>
              <h6>Form ID: {selectedFormValidation.formId}</h6>
              <p className="text-muted">
                Last Validated: {new Date(selectedFormValidation.status.lastValidated).toLocaleString('en-AU')}
              </p>
              
              {selectedFormValidation.status.valid ? (
                <Alert variant="success">
                  <i className="fas fa-check-circle me-2"></i>
                  <strong>ABN is Valid!</strong>
                  <br />
                  This ABN has been verified against the Australian Business Register.
                </Alert>
              ) : (
                <Alert variant="danger">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  <strong>ABN Validation Failed</strong>
                  <br />
                  {selectedFormValidation.status.error}
                </Alert>
              )}

              {selectedFormValidation.status.abrData && selectedFormValidation.status.abrData.success && (
                <div className="mt-3">
                  <h6>ABR Information:</h6>
                  <Row>
                    <Col md={6}>
                      <strong>Entity Name:</strong>
                      <div className="text-muted">{selectedFormValidation.status.abrData.entityName || 'N/A'}</div>
                    </Col>
                    <Col md={6}>
                      <strong>Entity Type:</strong>
                      <div className="text-muted">{selectedFormValidation.status.abrData.entityType || 'N/A'}</div>
                    </Col>
                  </Row>
                  <Row className="mt-2">
                    <Col md={6}>
                      <strong>Status:</strong>
                      <Badge bg={selectedFormValidation.status.abrData.isActive ? 'success' : 'danger'} className="ms-2">
                        {selectedFormValidation.status.abrData.status || 'N/A'}
                      </Badge>
                    </Col>
                    <Col md={6}>
                      <strong>GST Status:</strong>
                      <Badge bg={selectedFormValidation.status.abrData.isGSTRegistered ? 'success' : 'warning'} className="ms-2">
                        {selectedFormValidation.status.abrData.gstStatus || 'N/A'}
                      </Badge>
                    </Col>
                  </Row>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowValidationDetails(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default BASList;
